import { useState, useEffect } from 'react'
import { Modal } from '@components/UI/Modal'
import { Button } from '@components/UI/Button'
import { costiVolturaApi, type CostiVolturaResponse } from '@/services/api'
import styles from './CostiSubentroModal.module.css'

interface CostiVolturaModalProps {
  isOpen: boolean
  onClose: () => void
  onAccept: () => void
  showLuce: boolean
  showGas: boolean
}

export function CostiVolturaModal({
  isOpen,
  onClose,
  onAccept,
  showLuce,
  showGas
}: CostiVolturaModalProps) {
  const [costiData, setCostiData] = useState<CostiVolturaResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const [isAccepted, setIsAccepted] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadCosti()
      setHasScrolledToBottom(false)
      setIsAccepted(false)
    }
  }, [isOpen])

  const loadCosti = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await costiVolturaApi.get()
      setCostiData(data)
    } catch (err) {
      setError('Impossibile caricare i costi. Riprova.')
      console.error('Error loading costi voltura:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget
    const isAtBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 20
    if (isAtBottom) {
      setHasScrolledToBottom(true)
    }
  }

  const handleAccept = () => {
    if (isAccepted) {
      onAccept()
    }
  }

  if (!isOpen) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Costi di Voltura"
      size="lg"
      showClose={true}
    >
      <div className={styles.modalContent}>
        {loading ? (
          <div className={styles.loading}>
            <i className="bi bi-arrow-repeat" />
            <span>Caricamento costi...</span>
          </div>
        ) : error ? (
          <div className={styles.error}>
            <i className="bi bi-exclamation-triangle" />
            <span>{error}</span>
            <Button variant="secondary" size="sm" onClick={loadCosti}>
              Riprova
            </Button>
          </div>
        ) : costiData ? (
          <>
            <div className={styles.scrollArea} onScroll={handleScroll}>
              {/* Costi Gas */}
              {showGas && (
                <div className={styles.costiSection}>
                  <div className={styles.costiHeader}>
                    <i className="bi bi-fire" />
                    <span>{costiData.gas.titolo}</span>
                  </div>
                  <div className={styles.costiBody}>
                    {costiData.gas.voci.map((voce, index) => (
                      <div key={index} className={styles.costiItem}>
                        <div className={styles.costiItemContent}>
                          <span className={styles.costiLabel}>{voce.descrizione}</span>
                          {voce.nota && (
                            <span className={styles.costiNota}>{voce.nota}</span>
                          )}
                        </div>
                        <span className={styles.costiValue}>{voce.importo_formattato}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Costi Luce */}
              {showLuce && (
                <div className={styles.costiSection}>
                  <div className={styles.costiHeader}>
                    <i className="bi bi-lightbulb" />
                    <span>{costiData.luce.titolo}</span>
                  </div>
                  <div className={styles.costiBody}>
                    {costiData.luce.voci.map((voce, index) => (
                      <div key={index} className={styles.costiItem}>
                        <div className={styles.costiItemContent}>
                          <span className={styles.costiLabel}>{voce.descrizione}</span>
                          {voce.nota && (
                            <span className={styles.costiNota}>{voce.nota}</span>
                          )}
                        </div>
                        <span className={styles.costiValue}>{voce.importo_formattato}</span>
                      </div>
                    ))}
                    {costiData.luce.nota_voltura && (
                      <p className={styles.notaSoglia}>{costiData.luce.nota_voltura}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Note Legali */}
              <div className={styles.noteLegali}>
                <div className={styles.notaHeader}>
                  <i className="bi bi-info-circle" />
                  <span>Note Legali ARERA</span>
                </div>
                <p className={styles.notaText}>{costiData.note_legali}</p>
              </div>

              {/* Nota Recesso */}
              <div className={styles.notaRecesso}>
                <div className={styles.notaHeader}>
                  <i className="bi bi-shield-check" />
                  <span>Diritto di Recesso</span>
                </div>
                <p className={styles.notaText}>{costiData.nota_recesso}</p>
              </div>

              {!hasScrolledToBottom && (
                <div className={styles.scrollHint}>
                  <i className="bi bi-chevron-double-down" />
                  <span>Scorri per continuare</span>
                </div>
              )}
            </div>

            {/* Checkbox Accettazione */}
            <div className={styles.acceptSection}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={isAccepted}
                  onChange={(e) => setIsAccepted(e.target.checked)}
                  disabled={!hasScrolledToBottom}
                  className={styles.checkbox}
                />
                <span className={styles.checkmark}>
                  <i className="bi bi-check" />
                </span>
                <span className={styles.checkboxText}>
                  <strong>Ho Compreso</strong>
                  <small>Dichiaro di aver letto e compreso i costi e le condizioni di voltura sopra riportate</small>
                </span>
              </label>
            </div>

            {/* Actions */}
            <div className={styles.actions}>
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
              >
                Annulla
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleAccept}
                disabled={!isAccepted}
                icon="check-circle"
              >
                Conferma
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  )
}

